import {
  Archive,
  File,
  FileAudio,
  FileCode2,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Presentation,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { fileExtension } from '@flowshare/shared';

const TYPES: Record<string, { icon: LucideIcon; className: string }> = {
  image: { icon: FileImage, className: 'text-sky-300 bg-sky-400/10 border-sky-400/15' },
  video: { icon: FileVideo, className: 'text-violet-300 bg-violet-400/10 border-violet-400/15' },
  audio: { icon: FileAudio, className: 'text-pink-300 bg-pink-400/10 border-pink-400/15' },
  archive: { icon: Archive, className: 'text-amber-300 bg-amber-400/10 border-amber-400/15' },
  code: { icon: FileCode2, className: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/15' },
  pdf: { icon: FileText, className: 'text-rose-300 bg-rose-400/10 border-rose-400/15' },
  doc: { icon: FileText, className: 'text-blue-300 bg-blue-400/10 border-blue-400/15' },
  sheet: { icon: FileSpreadsheet, className: 'text-green-300 bg-green-400/10 border-green-400/15' },
  slides: { icon: Presentation, className: 'text-orange-300 bg-orange-400/10 border-orange-400/15' },
  default: { icon: File, className: 'text-muted-foreground bg-muted/60 border-border' },
};

const EXTENSION_GROUPS: Record<string, keyof typeof TYPES> = {
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  avif: 'image',
  heic: 'image',
  svg: 'image',
  mp4: 'video',
  mov: 'video',
  mkv: 'video',
  webm: 'video',
  avi: 'video',
  mp3: 'audio',
  wav: 'audio',
  flac: 'audio',
  aac: 'audio',
  m4a: 'audio',
  ogg: 'audio',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  bz2: 'archive',
  pdf: 'pdf',
  doc: 'doc',
  docx: 'doc',
  txt: 'doc',
  md: 'doc',
  rtf: 'doc',
  xls: 'sheet',
  xlsx: 'sheet',
  csv: 'sheet',
  ppt: 'slides',
  pptx: 'slides',
  ts: 'code',
  tsx: 'code',
  js: 'code',
  jsx: 'code',
  json: 'code',
  html: 'code',
  css: 'code',
  py: 'code',
  rs: 'code',
  go: 'code',
  sql: 'code',
  yml: 'code',
  yaml: 'code',
  toml: 'code',
};

function fileTypeFor(name: string): { icon: LucideIcon; className: string } {
  return TYPES[EXTENSION_GROUPS[fileExtension(name)] ?? 'default'] ?? TYPES.default!;
}

export function FileTypeIcon({
  name,
  className,
  size = 'default',
}: {
  name: string;
  className?: string;
  size?: 'sm' | 'default' | 'lg';
}) {
  const type = fileTypeFor(name);
  const Icon = type.icon;
  const dimensions =
    size === 'lg' ? 'size-12 rounded-2xl' : size === 'sm' ? 'size-9 rounded-xl' : 'size-11 rounded-xl';
  const iconSize = size === 'lg' ? 'size-5' : size === 'sm' ? 'size-4' : 'size-[18px]';

  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center border shadow-[0_1px_0_0_rgba(255,255,255,0.06)_inset]',
        dimensions,
        type.className,
        className,
      )}
      aria-hidden="true"
    >
      <Icon className={iconSize} strokeWidth={1.75} />
    </span>
  );
}
